import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { UploadCloud, Download, Trash2, RefreshCw, Flame, FileImage, FileVideo, FileAudio, File as FileIcon, Users, Smartphone, MoreVertical, HardDriveDownload, Share2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { transferUpload, downloadFile, API } from "./upload";

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const ago = (ts) => {
  const s = Math.max(0, Date.now() / 1000 - ts);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};
const expiresIn = (ts, ttlH) => {
  const left = ts + ttlH * 3600 - Date.now() / 1000;
  return left > 3600 ? `${Math.floor(left / 3600)}h` : `${Math.max(1, Math.floor(left / 60))}m`;
};
const iconFor = (ct = "") => {
  if (ct.startsWith("image/")) return FileImage;
  if (ct.startsWith("video/")) return FileVideo;
  if (ct.startsWith("audio/")) return FileAudio;
  return FileIcon;
};

// Shared drop-box: everyone with Fun Time access sees + downloads the same files.
export default function FileTransfer() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [limits, setLimits] = useState(null);
  const [burn, setBurn] = useState(true);
  const [uploading, setUploading] = useState(null); // {name, pct, done, total}
  const [dragOver, setDragOver] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transfer/files`);
      const list = res.data.files || [];
      setFiles(list);
      setSelected((prev) => {
        const ids = new Set(list.map((f) => f.file_id));
        return new Set([...prev].filter((id) => ids.has(id)));
      });
    } catch { /* transient */ }
  }, []);

  useEffect(() => {
    load();
    axios.get(`${API}/transfer/limits`).then((r) => setLimits(r.data)).catch(() => {});
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const addFiles = async (fileList) => {
    const items = Array.from(fileList || []);
    if (!items.length) return;
    for (let i = 0; i < items.length; i++) {
      const f = items[i];
      setUploading({ name: f.name, pct: 0, done: i, total: items.length });
      try {
        await transferUpload(f, burn, (pct) => setUploading({ name: f.name, pct, done: i, total: items.length }));
        toast.success(`${f.name} shared`);
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(null);
    load();
  };

  const download = async (f) => {
    setBusyId(f.file_id);
    try {
      await downloadFile(`${API}/transfer/files/${f.file_id}/download`, f.filename);
    } catch {
      toast.error("Download failed — please try again");
    } finally {
      setBusyId(null);
      if (f.delete_after_download) setTimeout(load, 1500);
    }
  };

  const fetchBlob = async (f) => {
    const res = await axios.get(`${API}/transfer/files/${f.file_id}/download`, { responseType: "blob" });
    return new Blob([res.data], { type: f.content_type || "application/octet-stream" });
  };

  // PC: native "Save As" dialog (File System Access API), fallback to normal download.
  const saveAs = async (f) => {
    let handle;
    try {
      handle = await window.showSaveFilePicker({ suggestedName: f.filename });
    } catch (err) {
      return; // user cancelled the picker
    }
    setBusyId(f.file_id);
    try {
      const blob = await fetchBlob(f);
      const w = await handle.createWritable();
      await w.write(blob);
      await w.close();
      toast.success(`Saved ${f.filename}`);
    } catch (err) {
      toast.error("Save As failed — starting a normal download");
      download(f);
    } finally {
      setBusyId(null);
      if (f.delete_after_download) setTimeout(load, 1500);
    }
  };

  // Phone: native share sheet → "Save Image/Video" (Photos app) or "Save to Files".
  const saveToPhone = async (f) => {
    setBusyId(f.file_id);
    try {
      const blob = await fetchBlob(f);
      const file = new File([blob], f.filename, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: f.filename });
        toast.success("Pick “Save Image/Video” for Photos, or “Save to Files”");
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = f.filename;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not open the share sheet");
    } finally {
      setBusyId(null);
      if (f.delete_after_download) setTimeout(load, 1500);
    }
  };

  const remove = async (f) => {
    try {
      await axios.delete(`${API}/transfer/files/${f.file_id}`);
      toast.success("File removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const isAdmin = user?.role === "admin";

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = files.length > 0 && files.every((f) => selected.has(f.file_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(files.map((f) => f.file_id)));

  const bulkDownload = async () => {
    const items = files.filter((f) => selected.has(f.file_id));
    toast.success(`Downloading ${items.length} file${items.length > 1 ? "s" : ""}…`);
    for (const f of items) {
      try {
        await downloadFile(`${API}/transfer/files/${f.file_id}/download`, f.filename);
      } catch {
        toast.error(`${f.filename}: download failed`);
      }
      await new Promise((r) => setTimeout(r, 400));
    }
    setSelected(new Set());
    setTimeout(load, 1500);
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    const results = await Promise.allSettled(ids.map((id) => axios.delete(`${API}/transfer/files/${id}`)));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    if (ok === ids.length) toast.success(`Deleted ${ok} file${ok > 1 ? "s" : ""}`);
    else toast.error(`Deleted ${ok}/${ids.length} — some files could not be removed`);
    setSelected(new Set());
    load();
  };

  return (
    <div className="space-y-4" data-testid="file-transfer">
      <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-2.5 flex items-start gap-2" data-testid="transfer-info-banner">
        <Users className="w-4 h-4 text-sky-300 flex-shrink-0 mt-0.5" />
        <p className="font-mono text-[11px] text-sky-200 leading-relaxed">
          Shared drop-box — every user allowed into Fun Time sees and can download these files.
          Files auto-delete after {limits?.ttl_hours ?? 24}h. Your limit: {limits?.max_file_mb ?? 250} MB per file
          {isAdmin ? " (admin)" : ""}. Turn on “Delete after first download” so nothing stays on the server once it reaches the other device.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upload */}
        <Card className="border-border/60">
          <CardContent className="p-4 space-y-3">
            <p className="font-mono text-xs font-bold text-primary">1 · Share files</p>
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/10" : "border-border/70 hover:border-primary/60"}`}
              data-testid="transfer-dropzone"
            >
              <UploadCloud className="w-6 h-6 text-primary" />
              <span className="font-mono text-xs text-card-foreground">Drop any files here, or tap to choose</span>
              <span className="font-mono text-[10px] text-muted-foreground">Photos · Videos · Documents · Anything</span>
              <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="transfer-file-input" />
            </label>
            <label className="flex items-center gap-2 font-mono text-[11px] text-card-foreground">
              <Switch checked={burn} onCheckedChange={setBurn} data-testid="transfer-burn-switch" />
              <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-orange-400" /> Delete from server after first download</span>
            </label>
            {uploading && (
              <div className="space-y-1" data-testid="transfer-upload-progress">
                <p className="font-mono text-[10px] text-sky-300 truncate">
                  Uploading {uploading.done + 1}/{uploading.total}: {uploading.name} — {uploading.pct}%
                </p>
                <div className="h-2 rounded bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploading.pct}%` }} /></div>
              </div>
            )}

            {/* QR quick-open — scan on the phone to land right here (desktop only) */}
            <div className="hidden md:flex items-center gap-4 rounded-lg border border-border/60 bg-secondary/30 p-3 mt-2" data-testid="transfer-qr-section">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                <QRCodeSVG
                  value={`${window.location.origin}/fun-time?tab=file-transfer`}
                  size={104}
                  bgColor="#ffffff"
                  fgColor="#0b2a1c"
                  data-testid="transfer-qr-code"
                />
              </div>
              <div className="min-w-0">
                <p className="font-mono text-xs font-bold text-card-foreground flex items-center gap-1.5">
                  <Smartphone className="w-3.5 h-3.5 text-primary" /> Open on your phone
                </p>
                <p className="font-mono text-[10px] text-muted-foreground leading-relaxed mt-1">
                  Scan with your phone camera — it jumps straight to this File Transfer page.
                  Log in on the phone and your files are right there.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shared list */}
        <Card className="border-border/60">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs font-bold text-primary">2 · Shared files ({files.length})</p>
              <div className="flex items-center gap-2">
                {files.length > 0 && (
                  <label className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground cursor-pointer">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="transfer-select-all" />
                    Select all
                  </label>
                )}
                <Button size="sm" variant="ghost" className="h-7 font-mono text-[10px]" onClick={load} data-testid="transfer-refresh-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
              </div>
            </div>
            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2" data-testid="transfer-bulk-bar">
                <span className="font-mono text-[11px] text-primary font-bold">{selected.size} selected</span>
                <Button size="sm" className="btn-3d h-7 font-mono text-[10px]" onClick={bulkDownload} data-testid="transfer-bulk-download">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download all
                </Button>
                <Button size="sm" variant="outline" className="h-7 font-mono text-[10px] text-rose-400 border-rose-500/40 hover:text-rose-300" onClick={bulkDelete} data-testid="transfer-bulk-delete">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete all
                </Button>
                <Button size="sm" variant="ghost" className="h-7 font-mono text-[10px]" onClick={() => setSelected(new Set())} data-testid="transfer-bulk-clear">
                  Clear
                </Button>
              </div>
            )}
            {files.length === 0 ? (
              <p className="font-mono text-[11px] text-muted-foreground py-6 text-center" data-testid="transfer-empty">
                Nothing shared right now. Files uploaded from any device will appear here.
              </p>
            ) : (
              <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="transfer-files-list">
                {files.map((f, i) => {
                  const Icon = iconFor(f.content_type);
                  const mine = f.uploader_id === user?.id;
                  return (
                    <li key={f.file_id} className="flex items-center gap-3 rounded-lg border border-border/60 bg-secondary/30 px-3 py-2" data-testid={`transfer-file-${i}`}>
                      <Checkbox checked={selected.has(f.file_id)} onCheckedChange={() => toggleSelect(f.file_id)} className="flex-shrink-0" data-testid={`transfer-select-${f.file_id}`} />
                      <Icon className="w-5 h-5 text-primary flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-xs text-card-foreground truncate" title={f.filename}>{f.filename}</p>
                        <p className="font-mono text-[10px] text-muted-foreground">
                          {fmtSize(f.size)} · {mine ? "you" : f.uploader_name} · {ago(f.created_ts)} · expires in {expiresIn(f.created_ts, limits?.ttl_hours ?? 24)}
                          {f.download_count > 0 && ` · ${f.download_count}↓`}
                        </p>
                        {f.delete_after_download && (
                          <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase text-orange-300"><Flame className="w-3 h-3" /> deletes after download</span>
                        )}
                      </div>
                      <Button size="sm" className="btn-3d h-7 font-mono text-[10px] flex-shrink-0" disabled={busyId === f.file_id} onClick={() => download(f)} data-testid={`transfer-download-${f.file_id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> {busyId === f.file_id ? "…" : "Download"}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0" title="More options" data-testid={`transfer-options-${f.file_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 font-mono text-xs">
                          {typeof window.showSaveFilePicker === "function" && (
                            <DropdownMenuItem className="cursor-pointer" onClick={() => saveAs(f)} data-testid={`transfer-saveas-${f.file_id}`}>
                              <HardDriveDownload className="w-4 h-4 mr-2" /> Save As… (choose folder)
                            </DropdownMenuItem>
                          )}
                          {typeof navigator.share === "function" && (
                            <DropdownMenuItem className="cursor-pointer" onClick={() => saveToPhone(f)} data-testid={`transfer-share-${f.file_id}`}>
                              <Share2 className="w-4 h-4 mr-2" /> Save to Photos / Files (phone)
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="cursor-pointer text-rose-400 focus:text-rose-300" onClick={() => remove(f)} data-testid={`transfer-delete-${f.file_id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete from server
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
